import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDay } from "@/lib/format";
import { Chip, PageHeader, Spinner } from "@/components/rp";
import { Calendar, Entry, Lightbox, NewEntry } from "@/components/Diario";

const iso = (d) => d.toLocaleDateString("en-CA");
const ym = (d) => iso(d).slice(0, 7);

export default function AdminDiario() {
  const [month, setMonth] = useState(new Date());
  const [day, setDay] = useState(iso(new Date()));
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [target, setTarget] = useState("");
  const [box, setBox] = useState(null);
  useEffect(() => {
    api.get("/admin/diario", { params: { month: ym(month) } }).then((r) => { setData(r.data); setTarget((t) => t || r.data.orders[0]?.id || ""); }).catch((e) => toast.error(errMsg(e)));
  }, [month]);
  const entries = useMemo(() => (data?.entries || []).filter((e) => !filter || e.order_id === filter), [data, filter]);
  const byDay = useMemo(() => entries.reduce((m, e) => ({ ...m, [e.date]: [...(m[e.date] || []), e] }), {}), [entries]);
  if (!data) return <Spinner />;
  const replace = (e) => setData((d) => ({ ...d, entries: d.entries.map((x) => (x.id === e.id ? { ...x, ...e } : x)) }));
  const remove = async (e) => {
    if (!window.confirm("Eliminare questa giornata del diario?")) return;
    try { await api.delete(`/orders/${e.order_id}/diario/${e.id}`); setData((d) => ({ ...d, entries: d.entries.filter((x) => x.id !== e.id) })); } catch (err) { toast.error(errMsg(err)); }
  };
  const info = Object.fromEntries(data.orders.map((o) => [o.id, o]));
  const pick = (d) => { setDay(d); if (d.slice(0, 7) !== ym(month)) setMonth(new Date(`${d}T12:00`)); };
  return (
    <div className="space-y-6" data-testid="admin-diario-page">
      <PageHeader label="Diario di bordo" title="Calendario di tutti i cantieri" />
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1" data-testid="admin-diario-filter">
        <Chip active={!filter} onClick={() => setFilter("")} data-testid="admin-diario-filter-all">Tutti i cantieri</Chip>
        {data.orders.map((o) => <Chip key={o.id} active={filter === o.id} onClick={() => { setFilter(o.id); setTarget(o.id); }} data-testid={`admin-diario-filter-${o.id}`}>{o.code} · {o.client_name}</Chip>)}
      </div>
      <div className="grid lg:grid-cols-[minmax(280px,380px)_1fr] gap-6">
        <div className="rp-card p-5 self-start">
          <Calendar month={month} setMonth={setMonth} byDay={byDay} day={day} setDay={setDay} />
          <p className="text-xs text-[#757575] mt-3">{entries.length} giornat{entries.length === 1 ? "a registrata" : "e registrate"} nel mese</p>
        </div>
        <div className="space-y-3" data-testid="admin-diario-day">
          <p className="font-semibold">{fmtDay(day)}</p>
          <div className="rp-card p-4 space-y-3">
            <label className="rp-label block">Cantiere
              <select value={target} onChange={(e) => setTarget(e.target.value)} className="rp-field mt-1" data-testid="admin-diario-target">{data.orders.map((o) => <option key={o.id} value={o.id}>{o.code} · {o.title} · {o.client_name}</option>)}</select>
            </label>
            {target && <NewEntry oid={target} date={day} setDate={pick} onAdded={(e) => setData((d) => ({ ...d, entries: [{ ...e, order_code: info[target]?.code, order_title: info[target]?.title, client_name: info[target]?.client_name }, ...d.entries] }))} />}
          </div>
          {!(byDay[day] || []).length && <p className="text-sm text-[#757575]">Nessuna attività registrata in questo giorno.</p>}
          {(byDay[day] || []).map((e) => (
            <div key={e.id}>
              <Link to={`/admin/commesse/${e.order_id}`} className="rp-label hover:underline" data-testid={`admin-diario-order-${e.id}`}>{e.order_code} · {e.order_title} · {e.client_name}</Link>
              <div className="mt-1"><Entry e={e} oid={e.order_id} admin onSaved={replace} onRemove={remove} setBox={setBox} /></div>
            </div>
          ))}
        </div>
      </div>
      <Lightbox box={box} setBox={setBox} />
    </div>
  );
}
